/**
 * Block 14 «Раскрой» — PR #21 EP-5 (CUT-5.1). 16 показателей экрана «Результат»
 * из `OptimizationResult` (движок) + `parts` (детерминированные кромки/пазы).
 *
 * ⚠️ НЕ путать с `cuttingMetrics.ts` (метрики ДЕТАЛЕЙ для инфо-панели PartsTab:
 * площадь/кромки/пазы). Здесь — итоговые метрики РАСКЛАДКИ (КИМ, листы, остатки,
 * отходы…). Длина кромки 1/2 и Длина пазов ПЕРЕИСПОЛЬЗУЮТ `cuttingMetrics`
 * (`edgeLength`/`grooveLength`) — это свойства деталей, не зависят от раскладки.
 *
 * Состав/порядок строк — вербатим прототипа (project.html:433-453); формулы и
 * пороги согласованы с Aibek (STOP-pre #21): Q1 порог нового остатка
 * `min(w,h) ≥ minOffcutSize ?? 200` (cutting-engine.js:342), Q2 отходы = НЕТТО
 * (целые листы − детали − новые остатки), Q3 «Использовано остатков» =
 * «Кол-во используемых остатков» = число задействованных листов-остатков склада.
 *
 * Сырые числа (площади м², длины м); округление — в UI (ResultsTab).
 */

import type { OptimizationResult, Rect, SheetLayout } from '@pomoshnik/shared';
import type { Part } from '../model/types';
import { edgeLength, grooveLength } from './cuttingMetrics';

/** Порог годного остатка по короткой стороне, мм (вербатим cutting-engine.js:342). */
export const DEFAULT_MIN_OFFCUT_MM = 200;

const MM2_PER_M2 = 1e6;

/** 16 показателей экрана «Результат» (сырые числа; форматирование — в UI). */
export interface ResultMetrics {
  /** 1. Количество (целых) листов. */
  sheetCount: number;
  /** 2. Длина кромки 1 (type1), м. */
  edge1Length: number;
  /** 3. Длина кромки 2 (type2), м. */
  edge2Length: number;
  /** 4. Использовано остатков (листы-остатки склада, задействованные в раскладке). */
  usedOffcutCount: number;
  /** 5. КИМ, % (highlight-строка). */
  kimPercent: number;
  /** 6. Размер целого листа (мм); null если целых листов нет. */
  sheetSize: { length: number; width: number } | null;
  /** 7. Площадь одного целого листа, м². */
  oneSheetArea: number;
  /** 8. Площадь всех целых листов, м². */
  allSheetsArea: number;
  /** 9. Кол-во используемых остатков (= #4, деталь в раскрытии). */
  usedOffcutCount2: number;
  /** 10. Площадь используемых остатков (валовая площадь листов-остатков), м². */
  usedOffcutArea: number;
  /** 11. Длина резов, м. */
  cutLengthMeters: number;
  /** 12. Количество размещённых деталей (по всем листам). */
  partCount: number;
  /** 13. Площадь деталей (по всем листам), м². */
  partsArea: number;
  /** 14. Площадь новых годных остатков (freeRects ≥ порога, по всем листам), м². */
  newOffcutArea: number;
  /** 15. Площадь отходов (нетто на целых листах: лист − детали − новые остатки), м². */
  wasteArea: number;
  /** 16. Длина пазов, м. */
  grooveLength: number;
}

/** Площадь прямоугольника, мм². */
function area(r: Rect): number {
  return r.w * r.h;
}

/** Годный новый остаток: короткая сторона ≥ порога (Q1, cutting-engine.js:342). */
export function isUsableOffcut(r: Rect, threshold: number): boolean {
  return Math.min(r.w, r.h) >= threshold;
}

/** Σ площадей годных freeRects листа, мм². */
function usableFreeArea(sheet: SheetLayout, threshold: number): number {
  return sheet.freeRects
    .filter((r) => isUsableOffcut(r, threshold))
    .reduce((s, r) => s + area(r), 0);
}

/** Σ площадей размещённых деталей листа, мм². */
function placedArea(sheet: SheetLayout): number {
  return sheet.placements.reduce((s, p) => s + p.w * p.h, 0);
}

/**
 * Вычислить 16 показателей. `parts` — web-детали проекта (для кромок/пазов).
 * `minOffcutSize` — `settings.minOffcutSize` (порог годного остатка; дефолт 200).
 */
export function computeResultMetrics(
  result: OptimizationResult,
  parts: Part[],
  minOffcutSize?: number,
): ResultMetrics {
  const threshold = minOffcutSize ?? DEFAULT_MIN_OFFCUT_MM;
  const whole = result.sheets.filter((s) => !s.isOffcut);
  const off = result.sheets.filter((s) => s.isOffcut);
  const first = whole[0];

  const wholeGross = whole.reduce((s, sh) => s + sh.sheetLength * sh.sheetWidth, 0);
  const wholeParts = whole.reduce((s, sh) => s + placedArea(sh), 0);
  const wholeNewOffcut = whole.reduce((s, sh) => s + usableFreeArea(sh, threshold), 0);
  const wasteMm2 = Math.max(0, wholeGross - wholeParts - wholeNewOffcut);

  return {
    sheetCount: whole.length,
    edge1Length: edgeLength(parts, 'type1'),
    edge2Length: edgeLength(parts, 'type2'),
    usedOffcutCount: off.length,
    kimPercent: result.metrics.kim * 100,
    sheetSize: first ? { length: first.sheetLength, width: first.sheetWidth } : null,
    oneSheetArea: first ? (first.sheetLength * first.sheetWidth) / MM2_PER_M2 : 0,
    allSheetsArea: wholeGross / MM2_PER_M2,
    usedOffcutCount2: off.length,
    usedOffcutArea: off.reduce((s, sh) => s + sh.sheetLength * sh.sheetWidth, 0) / MM2_PER_M2,
    cutLengthMeters: result.metrics.cutLengthMeters,
    partCount: result.sheets.reduce((s, sh) => s + sh.placements.length, 0),
    partsArea: result.sheets.reduce((s, sh) => s + placedArea(sh), 0) / MM2_PER_M2,
    newOffcutArea:
      result.sheets.reduce((s, sh) => s + usableFreeArea(sh, threshold), 0) / MM2_PER_M2,
    wasteArea: wasteMm2 / MM2_PER_M2,
    grooveLength: grooveLength(parts),
  };
}
